import React, { ReactNode, useMemo } from 'react';
import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Flex,
  Group,
  HoverCard,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';

import classes from './RunStepCard.module.css';

import { AutomationStep } from '@/types/automationSteps.ts';
import StepCardTitle from '@/components/StepCard/StepCardTitle.tsx';
import StepCardTopLabel from '@/components/StepCard/StepCardTopLabel.tsx';
import { getStepCardStyles } from '@/components/StepCard/styles.ts';
import StyledMenu from '@/components/StyledMenu/StyledMenu.tsx';
import { HiDotsVertical } from 'react-icons/hi';
import { HiOutlineClock } from 'react-icons/hi2';
import { usePinnedStep } from '@/hooks/usePinnedStep.ts';
import { useHover } from '@mantine/hooks';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import {
  StepExecution,
  RetainedPriorScopeStepRun,
  StepRun,
  WaitingStepDependency,
} from '@/types/stepExecutions.ts';
import useRetrieveStepExecution from '@/hooks/api/stepExecutions/useRetrieveStepExecution.ts';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import { Run } from '@/types/runs.ts';
import { StepDisabledInfo } from '@/utils/stepUtils.ts';
import AppIcon from '@/components/AppIcon.tsx';
import CachedExecutionIndicator from '@/pages/Run/components/CachedExecutionIndicator.tsx';
import { getTerminalReasonPresentation } from '@/utils/terminalReasonPresentation.ts';
import { getBrowserFailurePresentation } from '@/utils/browserFailurePresentation.ts';
import { selectDisplayedStepRun } from '@/pages/Run/components/retryAttemptPresentation.ts';
import { AutoExpandPlan, getAutoExpandScrollTargetStepRunId } from '@/types/autoExpandPlan.ts';
import { useIsHistoricalScopePresentation } from '@/pages/Run/components/HistoricalScopePresentationContext.ts';

export interface StepCardProps {
  run: Run;
  step: AutomationStep;
  stepRuns: StepRun[];
  retainedPriorScope?: RetainedPriorScopeStepRun;
  index: number;
  parentSteps: StepRun[];
  allowHistoricalStepRuns?: boolean;
  disabledInfo?: StepDisabledInfo;
  waitingDependencies: WaitingStepDependency[];
  stepsById: Record<string, AutomationStep>;
  topLabelPlacement?: 'above' | 'inside';
  children: (props: childrenProps) => ReactNode;
  autoExpandPlan?: AutoExpandPlan | null;
  activeScopeParentStepRunId?: string;
}

type childrenProps = {
  isOpen: boolean;
  stepRun?: StepRun;
  loading: boolean;
  stepExecution?: StepExecution;
  isRetainedPriorScope: boolean;
};

const ERROR_STATUSES = [ExecutionStatus.Errors, ExecutionStatus.Cancelled];
const PARTIALLY_COMPLETED_STATUSES = [
  ExecutionStatus.ActiveWithErrors,
  ExecutionStatus.PartiallyCompleted,
];

export default function RunStepCard({
  run,
  step,
  stepRuns,
  retainedPriorScope,
  index,
  parentSteps,
  allowHistoricalStepRuns = false,
  disabledInfo,
  waitingDependencies,
  stepsById,
  topLabelPlacement = 'above',
  children,
  autoExpandPlan,
  activeScopeParentStepRunId,
}: StepCardProps) {
  const isHistoricalScopePresentation = useIsHistoricalScopePresentation();
  const selectedStepRun = useMemo(
    () => selectDisplayedStepRun(stepRuns, allowHistoricalStepRuns),
    [allowHistoricalStepRuns, stepRuns],
  );
  const displayedStepRun = selectedStepRun ?? retainedPriorScope?.source_step_run;
  const isDisplayedRetainedPriorScope = !selectedStepRun && !!retainedPriorScope && !!displayedStepRun;

  const stepExecutionIdToFetch = displayedStepRun?.step_execution
    ? undefined
    : displayedStepRun?.step_execution_id;
  const { data: fetchedStepExecution, isPending: isStepExecutionPending } =
    useRetrieveStepExecution(stepExecutionIdToFetch);
  const stepExecution = displayedStepRun?.step_execution ?? fetchedStepExecution;
  const loadingStepExecution = !!stepExecutionIdToFetch && isStepExecutionPending;
  const terminalReasonPresentation = getTerminalReasonPresentation(
    stepExecution,
    displayedStepRun?.full_path,
  );

  const status = useMemo(() => {
    if (!stepExecution) return 'default';
    if (terminalReasonPresentation.isProtectedScopeSkippedUpstream) return 'upstream-skipped';
    if (
      stepExecution.status === ExecutionStatus.Errors &&
      terminalReasonPresentation.isBlockedByExternalUpstream
    ) {
      return 'blocked';
    }
    if (ERROR_STATUSES.includes(stepExecution.status)) return 'error';
    if (PARTIALLY_COMPLETED_STATUSES.includes(stepExecution.status)) return 'partially-completed';
    return 'default';
  }, [
    stepExecution,
    terminalReasonPresentation.isBlockedByExternalUpstream,
    terminalReasonPresentation.isProtectedScopeSkippedUpstream,
  ]);

  const [isOpen, setOpen] = React.useState(false);
  const appliedAutoExpandPlanRef = React.useRef<AutoExpandPlan | null>(null);
  const scrolledAutoExpandPlanRef = React.useRef<AutoExpandPlan | null>(null);
  const autoExpandScrollTargetStepRunId = getAutoExpandScrollTargetStepRunId(autoExpandPlan);

  React.useEffect(() => {
    if (
      autoExpandPlan !== appliedAutoExpandPlanRef.current &&
      displayedStepRun &&
      autoExpandPlan?.step_run_ids_to_open.includes(displayedStepRun.id)
    ) {
      setOpen(true);
      appliedAutoExpandPlanRef.current = autoExpandPlan;
    }
  }, [autoExpandPlan, displayedStepRun]);

  React.useEffect(() => {
    const scrollTargetStepRun = displayedStepRun;
    if (
      !autoExpandPlan ||
      autoExpandPlan === scrolledAutoExpandPlanRef.current ||
      !scrollTargetStepRun ||
      scrollTargetStepRun.id !== autoExpandScrollTargetStepRunId
    ) {
      return;
    }

    document.getElementById(`step-run-${scrollTargetStepRun.id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    scrolledAutoExpandPlanRef.current = autoExpandPlan;
  }, [autoExpandPlan, autoExpandScrollTargetStepRunId, displayedStepRun]);

  const { isStepPinned, togglePinnedStep } = usePinnedStep(step, run.id);

  const { hovered, ref: hoverRef } = useHover();

  const disabled = !displayedStepRun;
  const isConfiguredDisabled = !!disabledInfo?.isEffectivelyDisabled;
  const hasNoHistoricalExecutionRecord =
    disabled && isHistoricalScopePresentation && !isConfiguredDisabled;
  const stepCardStyles = getStepCardStyles(
    hasNoHistoricalExecutionRecord ? 'historical-missing-record' : disabled ? 'disabled' : status,
  );
  const showWaitingDependencies =
    disabled &&
    !disabledInfo?.isEffectivelyDisabled &&
    waitingDependencies.length > 0;

  const askUserStatus = stepExecution?.status === ExecutionStatus.AskUser;
  const blockedMessage = terminalReasonPresentation.message;
  const browserFailure = getBrowserFailurePresentation(stepExecution);
  const isScopeAnchor =
    activeScopeParentStepRunId !== undefined &&
    displayedStepRun?.id === activeScopeParentStepRunId;

  return (
    <Box
      id={displayedStepRun ? `step-run-${displayedStepRun.id}` : undefined}
      data-run-step-id={step.step_id}
      data-run-step-path={displayedStepRun?.full_path}
      data-scope-anchor={isScopeAnchor ? 'active' : undefined}
      style={{ position: 'relative', textAlign: 'left', width: '100%' }}
      ref={hoverRef}
    >
      <StepCardTopLabel
        step={step}
        index={index}
        parentSteps={parentSteps}
        runId={run.id}
        placement={topLabelPlacement}
      />

      <Accordion
        variant="filled"
        value={isOpen && !disabled ? step.step_id : null}
        onChange={(val) => setOpen(!!val && !disabled)}
        classNames={{
          panel: classes.accordionPanel,
          control: classes.accordionControl,
          label: classes.accordionLabel,
          content: classes.accordionContent,
        }}
        chevronIconSize={disabled ? 0 : 16}
      >
        <Accordion.Item
          value={step.step_id}
          style={{ ...stepCardStyles }}
          className={[
            isScopeAnchor ? classes.scopeAnchorItem : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {askUserStatus && (
            <Flex px={'md'} py="xs" bg={'#F2F0FD'}>
              <Text size={'11px'}>This step is paused and needs input before continuing</Text>
            </Flex>
          )}
          {blockedMessage && (
            <Flex
              px="md"
              py="xs"
              bg="#F4F4F4"
              style={{ borderTopLeftRadius: 7, borderTopRightRadius: 7 }}
            >
              <Text size="11px">{blockedMessage}</Text>
            </Flex>
          )}
          {stepExecution?.completion_disposition === 'recovered' && (
            <Flex px="md" py="xs" bg="#FFF4E6">
              <Text size="11px">The original steps did not complete. Response steps finished, so this workflow continued.</Text>
            </Flex>
          )}
          {browserFailure && (
            <Box px="md" py="xs" bg="orange.0">
              <Text size="xs">{browserFailure.message}</Text>
              <Text size="xs" c="dimmed">{browserFailure.nextAction}</Text>
            </Box>
          )}
          <Box pos="relative">
            <Accordion.Control
              style={{ padding: '12px 16px' }}
              disabled={disabled && !showWaitingDependencies}
            >
              <Group
                justify={'space-between'}
                wrap={'nowrap'}
                style={{ width: '100%', minWidth: 0, paddingRight: '48px' }}
              >
                <StepCardTitle
                  step={step}
                  status={stepExecution?.status}
                  terminalReasonCode={stepExecution?.terminal_reason_code}
                  terminalReasonContext={stepExecution?.terminal_reason_context}
                  currentStepPath={displayedStepRun?.full_path}
                  isUpstreamSkipped={terminalReasonPresentation.isProtectedScopeSkippedUpstream}
                  allowConfiguredIntegrationFallback={true}
                  statusAdornment={
                    showWaitingDependencies ? (
                      <WaitingDependenciesIndicator
                        dependencies={waitingDependencies}
                        stepsById={stepsById}
                        runStatus={run.status}
                      />
                    ) : null
                  }
                />
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  {terminalReasonPresentation.isProtectedScopeSkippedUpstream && (
                    <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>
                      Blocked upstream
                    </Badge>
                  )}
                  {isConfiguredDisabled && (
                    <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>
                      Disabled
                    </Badge>
                  )}
                  {hasNoHistoricalExecutionRecord && (
                    <Badge size="xs" color="gray" variant="light" style={{ flexShrink: 0 }}>
                      No execution record in this attempt
                    </Badge>
                  )}
                  {displayedStepRun?.used_existing_step_execution && (
                    <CachedExecutionIndicator
                      label={
                        displayedStepRun.is_preserved_rerun_carrier
                          ? 'Reused preserved rerun work'
                          : 'Used cached step execution'
                      }
                    />
                  )}
                </Group>
              </Group>
            </Accordion.Control>
            {hovered && !disabled && togglePinnedStep && (
              <Box
                pos="absolute"
                top="50%"
                right={36}
                style={{ transform: 'translateY(-50%)', zIndex: 1 }}
              >
                <StyledMenu position="left" withinPortal={false}>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={isStepPinned ? 'Un-pin step' : 'Pin step'}
                    >
                      <HiDotsVertical />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown w="150px">
                    <Menu.Item onClick={togglePinnedStep}>
                      {isStepPinned ? 'Un-pin step' : 'Pin Step'}
                    </Menu.Item>
                  </Menu.Dropdown>
                </StyledMenu>
              </Box>
            )}
          </Box>
          <Accordion.Panel>
            <Stack mb={'-xs'}>
              <CustomLoadingOverlay visible={loadingStepExecution} />
              {(children as (props: childrenProps) => ReactNode)({
                isOpen: isOpen,
                stepRun: displayedStepRun,
                loading: loadingStepExecution,
                stepExecution: stepExecution,
                isRetainedPriorScope: isDisplayedRetainedPriorScope,
              })}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Box>
  );
}

const WaitingDependenciesIndicator = ({
  dependencies,
  stepsById,
  runStatus,
}: {
  dependencies: WaitingStepDependency[];
  stepsById: Record<string, AutomationStep>;
  runStatus: ExecutionStatus;
}) => {
  const visibleDependencies = dependencies.slice(0, 5);
  const hiddenDependencyCount = dependencies.length - visibleDependencies.length;
  const title = ActiveStatuses.includes(runStatus) ? 'Waiting on' : 'Blocked by';
  const triggerLabel = 'Show step dependencies';
  const scrollToDependencyStep = (dependency: WaitingStepDependency) => {
    const stepElement = Array.from(
      document.querySelectorAll<HTMLElement>('[data-run-step-path], [data-run-step-id]'),
    ).find(
      (element) =>
        element.dataset.runStepPath === dependency.full_path ||
        element.dataset.runStepId === dependency.step_id,
    );

    if (!stepElement) return;

    stepElement.classList.remove(classes.scrollHighlight);
    void stepElement.offsetWidth;
    stepElement.classList.add(classes.scrollHighlight);
    window.setTimeout(() => stepElement.classList.remove(classes.scrollHighlight), 1400);
    stepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <HoverCard width={280} shadow="md" openDelay={200} withinPortal zIndex={1000}>
      <HoverCard.Target>
        <span
          aria-label={triggerLabel}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.stopPropagation();
            }
          }}
          role="button"
          tabIndex={0}
          style={{
            alignItems: 'center',
            color: '#757575',
            display: 'inline-flex',
            flexShrink: 0,
            height: 20,
            justifyContent: 'center',
            width: 20,
          }}
        >
          <AppIcon Icon={HiOutlineClock} size={17} iconProps={{ strokeWidth: 1.8 }} />
        </span>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={8}>
          <Text size="xs" fw={600}>
            {title}
          </Text>
          <Stack gap={6}>
            {visibleDependencies.map((dependency) => (
              <UnstyledButton
                key={dependency.full_path}
                onClick={() => scrollToDependencyStep(dependency)}
                style={{
                  borderRadius: 4,
                  display: 'block',
                  padding: '3px 4px',
                  width: '100%',
                }}
              >
                <Group gap={6} wrap="nowrap">
                  <DependencyStatusDot status={dependency.status} />
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="xs" truncate="end">
                      {stepsById[dependency.step_id]?.name || dependency.step_id}
                    </Text>
                    <Text size="10px" c="dimmed" truncate="end">
                      {getDependencyStatusLabel(dependency.status)}
                    </Text>
                  </Stack>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
          {hiddenDependencyCount > 0 && (
            <Text size="xs" c="dimmed">
              +{hiddenDependencyCount} more
            </Text>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

const getDependencyStatusLabel = (status: ExecutionStatus | null) => {
  if (status === ExecutionStatus.AskUser) return 'Needs input';
  if (status === ExecutionStatus.Errors) return 'Failed';
  if (status === ExecutionStatus.Cancelled) return 'Cancelled';
  if (status === ExecutionStatus.PartiallyCompleted) return 'Completed with issues';
  if (status === ExecutionStatus.Completed) return 'Completed';
  if (status && ActiveStatuses.includes(status)) return 'Running';
  return 'Waiting';
};

const DependencyStatusDot = ({ status }: { status: ExecutionStatus | null }) => {
  const color =
    status === ExecutionStatus.Completed
      ? '#0fa357'
      : status === ExecutionStatus.AskUser
        ? '#6F74F1'
      : status === ExecutionStatus.PartiallyCompleted
        ? '#FFBF00'
      : status && ActiveStatuses.includes(status)
        ? '#6F74F1'
        : status === ExecutionStatus.Errors || status === ExecutionStatus.Cancelled
          ? '#E03131'
          : '#9A9A9A';

  return (
    <Box
      style={{
        backgroundColor: color,
        borderRadius: '50%',
        flexShrink: 0,
        height: 7,
        width: 7,
      }}
    />
  );
};
